/**
 * F31 (Wave 2B) — admin skill-disputes + per-user skill-rating API client.
 *
 * Endpoints (F31 plan §7.2):
 *   GET    /admin/skill-disputes                                       → listDisputes (paginated)
 *   GET    /admin/skill-disputes/:id                                   → disputeDetail
 *   POST   /admin/skill-disputes/:id/resolve                           → resolveDispute
 *   GET    /admin/users/:userId/skill-ratings                          → userSkillRatings
 *   GET    /admin/users/:userId/skill-ratings/:sportName/history       → userRatingHistory
 *   POST   /admin/users/:userId/skill-ratings/:sportName/override      → overrideRating
 *
 * Permission gating happens server-side. The page-level <ProtectedRoute> gates
 * which routes a given admin role can navigate to; the API client forwards
 * requests and relies on the server to enforce RBAC (skill_disputes:resolve,
 * skill_ratings:override).
 *
 * Component tests get the in-memory mock via the vitest alias in
 * `apps/admin/vitest.config.ts` rewriting `'./api/skillDisputes'`.
 */
import type {
  AdminRatingOverrideInput,
  MatchOutcomeDetail,
  MatchOutcomeStatus,
  RatingHistoryEntry,
  ResolveMatchDisputeInput,
  SkillRatingSummary,
} from '@bravemove/types';
import type { SportName } from '@bravemove/design-tokens/icons';
import { adminFetch, buildQuery } from '../auth/apiClient';

// ─── Response envelope shapes ────────────────────────────────────────────────

/** One row in the admin skill-disputes queue. */
export interface AdminSkillDisputeRow {
  readonly id: string;
  readonly bookingId: string;
  readonly sport: SportName;
  readonly status: MatchOutcomeStatus;
  readonly disputedByUserId: string | null;
  readonly disputeReason: string | null;
  readonly disputedAt: string | null;
  readonly reportedByUserId: string | null;
  readonly reportedAt: string;
}

export interface AdminSkillDisputesListResponse {
  readonly data: readonly AdminSkillDisputeRow[];
  readonly pagination: {
    readonly page: number;
    readonly perPage: number;
    readonly total: number;
  };
}

/** Full dispute detail — the match outcome + the participant testimony. */
export type AdminSkillDisputeDetail = MatchOutcomeDetail;

export interface AdminUserSkillRatingsResponse {
  readonly userId: string;
  readonly displayName: string | null;
  readonly ratings: readonly SkillRatingSummary[];
}

export interface ListDisputesOpts {
  readonly status?: 'disputed' | 'resolved_by_admin' | 'all';
  readonly page?: number;
  readonly perPage?: number;
}

interface DataEnvelope<T> {
  readonly data: T;
}

// ─── API surface ─────────────────────────────────────────────────────────────

export const adminSkillDisputesApi = {
  async listDisputes(opts: ListDisputesOpts = {}): Promise<AdminSkillDisputesListResponse> {
    const qs = buildQuery({
      status: opts.status === 'all' ? undefined : (opts.status ?? 'disputed'),
      page: opts.page,
      perPage: opts.perPage,
    });
    return adminFetch<AdminSkillDisputesListResponse>(`/admin/skill-disputes${qs}`);
  },

  async disputeDetail(id: string): Promise<AdminSkillDisputeDetail | null> {
    try {
      const body = await adminFetch<DataEnvelope<AdminSkillDisputeDetail>>(
        `/admin/skill-disputes/${encodeURIComponent(id)}`,
      );
      return body.data;
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 404) return null;
      throw err;
    }
  },

  async resolveDispute(
    id: string,
    input: ResolveMatchDisputeInput,
  ): Promise<AdminSkillDisputeDetail> {
    const body = await adminFetch<DataEnvelope<AdminSkillDisputeDetail>>(
      `/admin/skill-disputes/${encodeURIComponent(id)}/resolve`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  async userSkillRatings(userId: string): Promise<AdminUserSkillRatingsResponse> {
    // Backend: GET /admin/users/:userId/skill-ratings (routes/admin/
    // users-skills.ts, perm users:read) wraps the projection in a `{ data }`
    // envelope like every other admin detail route (TGS-05).
    const body = await adminFetch<DataEnvelope<AdminUserSkillRatingsResponse>>(
      `/admin/users/${encodeURIComponent(userId)}/skill-ratings`,
    );
    return body.data;
  },

  async userRatingHistory(userId: string, sportName: SportName): Promise<RatingHistoryEntry[]> {
    const body = await adminFetch<DataEnvelope<readonly RatingHistoryEntry[]>>(
      `/admin/users/${encodeURIComponent(userId)}/skill-ratings/${encodeURIComponent(
        sportName,
      )}/history`,
    );
    return [...body.data];
  },

  async overrideRating(
    userId: string,
    sportName: SportName,
    input: AdminRatingOverrideInput,
  ): Promise<SkillRatingSummary> {
    const body = await adminFetch<DataEnvelope<SkillRatingSummary>>(
      `/admin/users/${encodeURIComponent(userId)}/skill-ratings/${encodeURIComponent(
        sportName,
      )}/override`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },
};
